import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useEffect, Suspense, lazy } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Navbar } from './components/layout/Navbar';
import { Footer } from './components/layout/Footer';
import { PageTracker } from './components/analytics/PageTracker';

// Critical pages - load immediately
import Home from './pages/Home';
import About from './pages/About';
import Services from './pages/Services';
import NotFound from './pages/NotFound';

// Lazy load non-critical pages - simplified approach
const Team = lazy(() => import('./pages/Team'));
const Contact = lazy(() => import('./pages/Contact'));
const BookOnline = lazy(() => import('./pages/BookOnline'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const Capabilities = lazy(() => import('./pages/Capabilities'));
const HardwareSales = lazy(() => import('./pages/HardwareSales'));
const SoftwareLicensing = lazy(() => import('./pages/SoftwareLicensing'));

// Dashboard pages - lazy load
const Dashboard = lazy(() => import('./pages/Dashboard'));
const TrafficAnalytics = lazy(() => import('./pages/dashboard/TrafficAnalytics'));
const LeadManagement = lazy(() => import('./pages/dashboard/LeadManagement'));
const Performance = lazy(() => import('./pages/dashboard/Performance'));
const Settings = lazy(() => import('./pages/dashboard/Settings'));

// Capability pages - lazy load
const CustomSecurityTools = lazy(() => import('./pages/capabilities/CustomSecurityTools'));
const AIVoiceSystems = lazy(() => import('./pages/capabilities/AIVoiceSystems'));
const EnterpriseSecurityArchitecture = lazy(() => import('./pages/capabilities/EnterpriseSecurityArchitecture'));
const InfrastructureAutomation = lazy(() => import('./pages/capabilities/InfrastructureAutomation'));
const NetworkDesign = lazy(() => import('./pages/capabilities/NetworkDesign'));
const ComplianceManagement = lazy(() => import('./pages/capabilities/ComplianceManagement'));
const CloudSecurity = lazy(() => import('./pages/capabilities/CloudSecurity'));
const NetworkCompliance = lazy(() => import('./pages/capabilities/NetworkCompliance'));
const IoTDroneDevelopment = lazy(() => import('./pages/capabilities/IoTDroneDevelopment'));
const CryptoInfrastructure = lazy(() => import('./pages/capabilities/CryptoInfrastructure'));
const EnterpriseTraining = lazy(() => import('./pages/capabilities/EnterpriseTraining'));
const LegacyModernization = lazy(() => import('./pages/capabilities/LegacyModernization'));
const FileIntegrityMonitoring = lazy(() => import('./pages/capabilities/FileIntegrityMonitoring'));
const IdentityManagement = lazy(() => import('./pages/capabilities/IdentityManagement'));
const AdaptiveAIDefense = lazy(() => import('./pages/AdaptiveAIDefense'));

// Loading component
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
  </div>
);

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App() {
  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <PageTracker>
        <Routes>
          {/* Admin Dashboard Routes - No main site layout */}
          <Route path="/admin/dashboard" element={
            <Suspense fallback={<LoadingSpinner />}>
              <Dashboard />
            </Suspense>
          } />
          <Route path="/admin/dashboard/traffic" element={
            <Suspense fallback={<LoadingSpinner />}>
              <TrafficAnalytics />
            </Suspense>
          } />
          <Route path="/admin/dashboard/leads" element={
            <Suspense fallback={<LoadingSpinner />}>
              <LeadManagement />
            </Suspense>
          } />
          <Route path="/admin/dashboard/performance" element={
            <Suspense fallback={<LoadingSpinner />}>
              <Performance />
            </Suspense>
          } />
          <Route path="/admin/dashboard/settings" element={
            <Suspense fallback={<LoadingSpinner />}>
              <Settings />
            </Suspense>
          } />
          
          {/* Main Site Routes - With navbar and footer */}
          <Route path="/*" element={
            <div className="min-h-screen bg-gray-50">
              <Navbar />
              <main>
                <Suspense fallback={<LoadingSpinner />}>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/services" element={<Services />} />
                    <Route path="/capabilities" element={<Capabilities />} />
                    <Route path="/hardware-sales" element={<HardwareSales />} />
                    <Route path="/software-licensing" element={<SoftwareLicensing />} />
                    <Route path="/team" element={<Team />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/book" element={<BookOnline />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/capabilities/custom-security-tools" element={<CustomSecurityTools />} />
                    <Route path="/capabilities/ai-voice-systems" element={<AIVoiceSystems />} />
                    <Route path="/capabilities/enterprise-security" element={<EnterpriseSecurityArchitecture />} />
                    <Route path="/capabilities/infrastructure-automation" element={<InfrastructureAutomation />} />
                    <Route path="/capabilities/network-design" element={<NetworkDesign />} />
                    <Route path="/capabilities/compliance-management" element={<ComplianceManagement />} />
                    <Route path="/capabilities/cloud-security" element={<CloudSecurity />} />
                    <Route path="/capabilities/network-compliance" element={<NetworkCompliance />} />
                    <Route path="/capabilities/iot-drone-development" element={<IoTDroneDevelopment />} />
                    <Route path="/capabilities/crypto-infrastructure" element={<CryptoInfrastructure />} />
                    <Route path="/capabilities/enterprise-training" element={<EnterpriseTraining />} />
                    <Route path="/capabilities/legacy-modernization" element={<LegacyModernization />} />
                    <Route path="/capabilities/file-integrity-monitoring" element={<FileIntegrityMonitoring />} />
                    <Route path="/capabilities/identity-management" element={<IdentityManagement />} />
                    <Route path="/adaptive-ai-defense" element={<AdaptiveAIDefense />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </main>
              <Footer />
            </div>
          } />
        </Routes>
        </PageTracker>
      </Router>
    </HelmetProvider>
  );
}

export default App;
